<template>
  <div
    class="card-container">

    <!-- CARD CONTENTS -->
    <div class="card-contents">

      <div class="card-body">
        <div class="card-body-main">

          <!-- TITLE -->
          <div class="card-title" @mousedown.self.prevent> <!-- prevents focussing on contenteditable span -->
            <div class="card-subheader">
              title
            </div>
            <div
              v-if="!editingTitle"
              class="card-title-text"
              @click="editTitle">
              {{ currentCard.title || 'draft' }}
            </div>
            <input
              v-else
              type="text"
              class="title-input"
              ref="titleInput"
              spellcheck="false"
              @blur="updateTitle"
              @keydown.enter="(e) => e.target.blur()"
              :value="currentCard.title" />
          </div>

          <!-- ACTIONS -->
          <div class="card-actions">
            <i :class="starsIcon" @click="incrementStars" style="cursor: pointer;" />
            <i class="mdi mdi-dots-horizontal dots-menu" @mouseenter="toggleShowActionsMenu(true)" @mouseleave="toggleShowActionsMenu(false)" />
            <transition name="fade">
              <div
                class="collection-list-item-menu"
                @mouseenter="toggleShowActionsMenu(true)"
                @mouseleave="toggleShowActionsMenu(false)"
                v-show="showActionsMenu">
                <div
                  class="collection-list-item-menu-item"
                  @click="$emit('move-card', currentCard.id)">
                  Move
                </div>
                <div class="collection-list-item-menu-divider" />
                <div
                  class="collection-list-item-menu-item"
                  style="color: red"
                  @click="deleteCard">
                  Delete
                </div>
              </div>
            </transition>
          </div>

          <!-- ITEM -->
          <div class="card-item-container" @mouseenter="setItemHovered(true)" @mouseleave="setItemHovered(false)">
            <div class="card-subheader">
              item
            </div>
            <span
              v-if="currentCard.item"
              class="card-item"
              @mousedown.prevent
              @click.exact="currentCard.item && currentCard.item.type === 'webpage'
                ? $navigateTo({ externalUrl: currentCard.item.address })
                : $goToItem(currentCard)"
              @click.middle.exact="currentCard.item && currentCard.item.type === 'webpage'
                ? $navigateTo({ externalUrl: currentCard.item.address })
                : openItemInBackground(currentCard)"
              @click.ctrl.exact="currentCard.item && currentCard.item.type === 'webpage'
                ? $navigateTo({ externalUrl: currentCard.item.address })
                : openItemInBackground(currentCard)">
              <i v-if="itemHovered" class="mdi delete-icon" :class="itemIsBeingDeleted ? 'mdi-timer-sand mdi-spin' : 'mdi-delete'" @click.stop="deleteItem" />
              <i v-else-if="currentCard.item.type === 'file'" class="mdi mdi-paperclip mdi-rotate-45" />
              <i v-else class="mdi mdi-open-in-new" />
              {{ currentCard.item.displayName }}
            </span>
            <span
              v-else-if="fileIsUploading">
              <i class="mdi mdi-timer-sand-empty mdi-spin" style="margin-right: 0.25rem;" />
              Uploading file...
            </span>
            <span
              v-else
              style="fontStyle: normal">
              No item.
              <span
                class="add-item-text"
                @click="addItem">
                Add item
              </span>
            </span>
            <b-modal
              v-model="displayAddItemPopup"
              @close="hideAddItemPopup"
              has-modal-card>
              <div class="modal-card">
                <header class="modal-card-head" style="font-style: normal;">
                  <p class="modal-card-title">
                    Add an item
                  </p>
                  <button
                    type="button"
                    class="delete"
                    @click="hideAddItemPopup" />
                </header>
                <section class="modal-card-body">
                  <b-upload
                    v-model="selectedFile"
                    @input="handleSelectFile"
                    drag-drop
                    expanded
                    accept=".pdf,.docx,.xlsx,.pptx,.png,.jpg">
                    <section class="section">
                      <div class="content has-text-centered">
                        <p>
                          <b-icon icon="upload" size="is-large" />
                        </p>
                        <p>
                          Drop a file here or click to upload
                        </p>
                      </div>
                    </section>
                  </b-upload>
                  <div>
                    <br><br>Or add a link to a webpage:<br>
                    <input
                      type="url"
                      class="url-input"
                      v-model="newItemUrl"
                      ref="newItemUrl"
                      @blur="handleAddUrl"
                      @keydown.enter="handleAddUrl"
                      placeholder="Enter an url, e.g. https://www.duckduckgo.com/" />
                  </div>
                </section>
                <footer class="modal-card-foot" v-if="waitingForId">
                  <i class="mdi mdi-timer-sand-empty mdi-16px mdi-spin" style="marginRight: 0.5rem" /> One moment please...
                </footer>
                <footer class="modal-card-foot" v-else>
                  <button
                    class="button"
                    type="button"
                    @click="hideAddItemPopup">
                    {{ Number($route.params.cardId) === 0 ? 'Skip' : 'Cancel' }}
                  </button>
                  <button
                    class="button is-primary"
                    type="button"
                    @click="handleAddUrl">
                    Save
                  </button>
                </footer>
              </div>
            </b-modal>
          </div>

          <!-- SECTION -->
          <div>
            <div class="card-subheader">
              section
            </div>
            <div
              v-for="section in sections"
              :key="section"
              class="card-section"
              :class="{
                'active-card-section': currentCard.section === section,
              }"
              @click="updateSection(section)">
              {{ section }}
              <span
                class="icon remove-card-section"
                @click.stop="updateSection(null)">
                <i class="mdi mdi-close" />
              </span>
            </div>
            <input
              class="add-card-section"
              type="text"
              placeholder="New section"
              v-model="newSection"
              @keyup.enter="addSection"
              @keydown.tab.prevent="addSection"
              @blur="addSection" />
          </div>

          <!-- DESCRIPTION -->
          <div
            class="card-description">
            <div class="card-subheader">
              description
            </div>
            <editor-floating-menu
              :editor="editor"
              class="floating-menu-container"
              v-slot="{ commands, isActive, menu }">
              <div
                class="floating-menu"
                :class="{ 'is-active': menu.isActive && !menuHidden }"
                :style="`top: ${menu.top}px`">
                <div
                  class="menu-item"
                  :class="{ 'is-active': isActive.bold() }"
                  @click="commands.bold">
                  <i class="mdi mdi-format-bold mdi-18px" />
                </div>
                <div
                  class="menu-item"
                  :class="{ 'is-active': isActive.italic() }"
                  @click="commands.italic">
                  <i class="mdi mdi-format-italic mdi-18px" />
                </div>
                <div
                  class="menu-item"
                  :class="{ 'is-active': isActive.underline() }"
                  @click="commands.underline">
                  <i class="mdi mdi-format-underline mdi-18px" />
                </div>
                <div
                  class="menu-item"
                  :class="{ 'is-active': isActive.bullet_list() }"
                  @click="commands.bullet_list">
                  <i class="mdi mdi-format-list-bulleted mdi-18px" />
                </div>
                <div
                  class="menu-item"
                  :class="{ 'is-active': isActive.ordered_list() }"
                  @click="commands.ordered_list">
                  <i class="mdi mdi-format-list-numbered mdi-18px" />
                </div>
                <div
                  class="menu-item"
                  :class="{ 'is-active': isActive.todo_list() }"
                  @click="commands.todo_list()">
                  <i class="mdi mdi-format-list-checks mdi-18px" />
                </div>
              </div>
            </editor-floating-menu>
            <editor-content
              class="editor"
              :editor="editor"
              ref="editor"
              v-scroll="handleDescriptionScroll"
              spellcheck="false" />
          </div>

          <!-- KEYWORDS -->
          <div class="card-keywords">
            <div class="card-subheader">
              keywords
            </div>
            <div
              v-for="keyword in currentCard.keywords"
              :key="keyword"
              class="keyword-container">
              <span class="keyword">
                {{ keyword }}
                <i
                  class="mdi mdi-close-circle-outline mdi-12px"
                  @click="deleteKeyword(keyword)" />
              </span>
              <div class="keyword-triangle" />
            </div>
            <span class="keyword add-keyword">
              <input
                class="new-keyword"
                v-model="newKeyword"
                ref="newKeywordInput"
                placeholder="add keyword..."
                @keydown.tab.prevent="addKeyword"
                @keyup.enter="addKeyword"
                @keyup.esc="cancelAddKeyword"
                @focus="newKeywordHasFocus = true"
                @blur="addKeyword" />
            </span>
            <div class="keyword-triangle" />
          </div>

          <!-- COLLECTION -->
          <div class="card-collection-container">
            <div class="card-subheader">
              collection
            </div>
            <div
              class="card-collection">
              <span @click="$goToCollection(currentCardCollection)">
                {{ currentCardCollection.name }}
              </span>
              <span
                class="move-card-text"
                @click.stop="$emit('move-card', currentCard)">
                Move
              </span>
            </div>
          </div>
        </div>

        <!-- DATES -->
        <div class="card-dates-container">
          <div class="card-date">
            <span>
              Created
            </span>
            <span>
              {{ currentCard.dateCreated && $formatDate(currentCard.dateCreated) }}
            </span>
          </div>
          <div class="card-date">
            <span>
              Last modified
            </span>
            <span>
              {{ currentCard.dateLastModified && $formatDate(currentCard.dateLastModified) }}
            </span>
          </div>
        </div>

      </div> <!-- END CARD BODY -->
    </div> <!-- END CARD CONTENTS -->

  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { Editor, EditorContent, EditorFloatingMenu } from 'tiptap';
import { HardBreak, Bold, Italic, Underline, BulletList, OrderedList, ListItem, TodoItem, TodoList, Link } from 'tiptap-extensions';
const debounce = require('lodash/debounce');

export default {
  name: 'CardView',
  components: {
    EditorContent,
    EditorFloatingMenu,
  },
  directives: {
    scroll: {
      inserted: function (el, binding) { // eslint-disable-line 
        let f = function (evt) { // eslint-disable-line 
          if (binding.value(evt, el)) {
            el.removeEventListener('scroll', f);
          }
        };
        el.addEventListener('scroll', f);
      },
    },
  },
  data() {
    return {
      // State
      displayAddItemPopup: false,
      fileIsUploading: false,
      itemIsBeingDeleted: false,
      menuHidden: false,
      newKeywordHasFocus: false,
      editingTitle: false,
      showActionsMenu: false,
      itemHovered: false,
      // New card stuff
      newCardIdPromise: null,
      newCardId: null,
      newCardObject: null,
      waitingForId: false,
      // Input
      newItemUrl: null,
      newKeyword: null,
      selectedFile: null,
      newSection: null,
      // Editor
      editor: new Editor({
        editable: true,
        extensions: [
          new HardBreak(),
          new Bold(),
          new Italic(),
          new Underline(),
          new BulletList(),
          new OrderedList(),
          new ListItem(),
          new TodoItem({ nested: true }),
          new TodoList(),
          new Link(),
        ],
        onUpdate: debounce(({ getHTML }) => {
          this.$updateCard({
            id: this.currentCard.id,
            data: {
              description: getHTML(),
            },
          });
        }, 500),
        onTransaction: () => {
          this.menuHidden = false;
        },
        content: (this.currentCard && this.currentCard.description),
      }),
    };
  },
  computed: {
    ...mapState({
      currentCardCollection: function(state) { // eslint-disable-line
        // The selectedCollection fallback is in case a card is added to a new collection that does not yet have an id, due to which currentCard still lacks a relatedCollectionId
        const fallback = { id: null, name: '-' };
        if (!this.currentCard) return fallback;

        const relatedCollectionId = this.currentCard.relatedCollectionId || this.selectedCollection || this.selectedCollectionFallback;
        return state.collections.collectionNodes[relatedCollectionId] || state.collections.archivedCollectionNodes[relatedCollectionId] || fallback;
      },
      sections: function(state) { // eslint-disable-line
      // Use a set to prevent duplicates, transform to array on return
        const sections = new Set();
        if (!this.currentCard) return [...sections];

        const relatedCollectionId = this.currentCard.relatedCollectionId || this.selectedCollection || this.selectedCollectionFallback;
        // New collections aren't automatically inserted in the cards state. Happens when first card is added. This to prevent problems.
        if (!state.cards.cards[relatedCollectionId]) return [];

        state.cards.cards[relatedCollectionId].forEach((card) => {
          if (card.section) sections.add(card.section);
        });
        return [...sections].sort();
      },
      selectedCollection: state => state.collections.selectedCollection,
      selectedCollectionFallback: state => Math.max(...Object.keys(state.collections.collectionNodes)),
      openItems: state => state.appState.openItems,
      storedSidebarWidth: state => state.appState.localSettings.sidebarWidth,
      introStep: state => state.appState.introStep,
    }),
    ...mapGetters({
      getCardRef: 'cards/getCardRef',
    }),
    currentCard() {
      const cardId = Number(this.$route.params.cardId);
      const relatedCollectionId = this.searchIsActive ? null : this.selectedCollection;
      return this.getCardRef({ cardId, relatedCollectionId });
    },
    starsIcon() {
      if (this.currentCard.stars === 1) return 'mdi mdi-star-outline mdi-18px';
      if (this.currentCard.stars === 2) return 'mdi mdi-star-half mdi-18px';
      if (this.currentCard.stars === 3) return 'mdi mdi-star mdi-18px';
      return 'mdi mdi-star-outline mdi-18px inactive';
    },
  },
  methods: {
    ...mapActions({
      vDeleteFile: 'files/deleteFile',
      deleteAnnotations: 'files/deleteAnnotations',
      getNewCardId: 'cards/getNewCardId',
      setError: 'notifications/setError',
      setNotification: 'notifications/setNotification',
      openInWebViewer: 'files/openInWebViewer',
      addNavigationToHistory: 'appState/addNavigationToHistory',
      showAddedItemInBackgroundIndicator: 'appState/showAddedItemInBackgroundIndicator',
      v_storeScrollPosition: 'appState/storeScrollPosition',
    }),
    ...mapMutations({
      CloseOpenItem: 'appState/CloseOpenItem',
      AddOpenItem: 'appState/AddOpenItem',
    }),
    toggleShowActionsMenu(show) {
      if (this.editingTitle) return;

      this.showActionsMenu = show;
    },
    setItemHovered(val) {
      if (this.editingTitle) return;

      this.itemHovered = val;
    },
    handleDescriptionScroll: debounce(function(evt, el) { // eslint-disable-line
      this.hideMenu();

      this.storeScrollPosition(evt, el);
    }, 50),
    storeScrollPosition(evt, el) {
      this.v_storeScrollPosition({ type: 'cardDescription', id: this.currentCard.id, scrollPosition: el.scrollTop });
    },
    openItemInBackground(card) {
      this.openInWebViewer(card);
      this.addNavigationToHistory(`/item/${card.id}`);
      this.showAddedItemInBackgroundIndicator();
    },
    async addItem() {
      this.displayAddItemPopup = true;
      await this.$nextTick();
      this.$refs.newItemUrl.focus();
    },
    addSection(event) {
      if (!this.newSection) return; // Tabbing away results in double calling this: for Tab and for blur. Prevent updating with null for second call.

      this.$updateCard({
        id: this.currentCard.id,
        data: {
          section: this.newSection,
        },
      });
      this.newSection = null;
      event.target.blur();
    },
    updateSection(section) {
      this.$updateCard({
        id: this.currentCard.id,
        data: {
          section,
        },
      });
    },
    incrementStars() {
      let stars = this.currentCard.stars + 1;
      if (stars > 3) stars = 0;
      this.$updateCard({
        id: this.currentCard.id,
        data: {
          stars,
        },
      });
    },
    editTitle(e) {
      const selObj = document.getSelection();
      let startPosition = Math.min(selObj.anchorOffset, selObj.focusOffset) - 1;
      let endPosition = Math.max(selObj.anchorOffset, selObj.focusOffset) - 1;

      if (startPosition === endPosition) {
        // No selected text
        startPosition = false;
        endPosition = false;
      }

      this.editingTitle = true;
      this.selectCardTitle(startPosition, endPosition);
    },
    updateTitle(e) {
      this.editingTitle = false;
      if (this.currentCard.title === e.target.value) return;

      this.$updateCard({
        id: this.currentCard.id,
        data: {
          title: e.target.value.trim() || '(no title)',
        },
      });
    },
    async selectCardTitle(startPosition, endPosition) {
      await this.$nextTick();
      const inputField = this.$refs.titleInput;

      inputField.focus();
      const start = startPosition || 0;
      const end = endPosition || (this.currentCard.title && this.currentCard.title.length) || 0;
      inputField.setSelectionRange(start, end);
    },
    async deleteItem() {
      this.itemIsBeingDeleted = true;

      if (this.currentCard.item.type === 'file') {
        await Promise.all([
          this.vDeleteFile({ cardId: this.currentCard.id, relatedCollectionId: this.currentCard.relatedCollectionId, filename: this.currentCard.item.address }),
          this.deleteAnnotations(this.currentCard.id),
        ]);
      }
      this.$updateCard({
        id: this.currentCard.id,
        data: {
          item: null,
        },
      });

      this.itemIsBeingDeleted = false;
    },
    async handleAddUrl() {
      if (this.waitingForId) return; // do not execute if we already are waiting for an id (indicates that another action is already underway, e.g. handleSelectFile/handleAddUrl/hideModal)
      if (!this.newItemUrl) return;

      // If we were adding a new card, set it now (adds it to Vuex)
      if (this.currentCard.id === 0) {
        this.waitingForId = true;
        this.newCardId = await this.newCardIdPromise;
        this.setNewCard();
        this.waitingForId = false;
        this.$goToCard({ id: this.newCardId });
        this.editTitle();
      }

      this.$updateCard({
        id: this.newCardId || this.currentCard.id,
        data: {
          item: {
            type: 'webpage',
            address: this.newItemUrl,
            displayName: this.newItemUrl,
          },
        },
      });
      this.newItemUrl = null;
      this.displayAddItemPopup = false;
    },
    async handleAddUrlFromExtension() {
      if (this.waitingForId) return; // do not execute if we already are waiting for an id (indicates that another action is already underway, e.g. handleSelectFile/handleAddUrl/hideModal)
      if (!this.$route.query.url) return;

      const { url, title } = this.$route.query;
      this.newItemUrl = url;

      this.waitingForId = true;
      this.newCardId = await this.newCardIdPromise;
      this.setNewCard();
      this.waitingForId = false;
      this.$updateCard({
        id: this.newCardId,
        data: {
          title,
        },
      });
      this.$goToCard({ id: this.newCardId });
      this.editTitle();

      this.$updateCard({
        id: this.newCardId || this.currentCard.id,
        data: {
          item: {
            type: 'webpage',
            address: this.newItemUrl,
            displayName: this.newItemUrl,
          },
        },
      });
      this.newItemUrl = null;
      this.displayAddItemPopup = false;
    },
    async handleSelectFile() {
      if (this.waitingForId) return; // do not execute if we already are waiting for an id (indicates that another action is already underway, e.g. handleSelectFile/handleAddUrl/hideModal)

      const file = this.selectedFile;
      if (!file) return;
      this.fileIsUploading = true;
      const { name } = file;

      // If we're adding a new card, save a title based on the filename in the draft object and add the card to Vuex
      if (this.currentCard.id === 0) {
        this.waitingForId = true;
        this.newCardId = await this.newCardIdPromise;
        this.setNewCard();
        this.$updateCard({
          id: this.newCardId,
          data: {
            title: name.slice(0, name.lastIndexOf('.')),
          },
        });
        this.waitingForId = false;
        if (this.currentCard.id === 0) this.$goToCard({ id: this.newCardId });
        this.editTitle();
      }

      // If the title equals 'draft' (default) or 'no title' (when user gave an empty title) or literally '', give a new title (e.g. when adding a new card)
      if (this.currentCard.title === '' || this.currentCard.title === 'draft' || this.currentCard.title === '(no title)') {
        this.$updateCard({
          id: this.currentCard.id,
          data: {
            title: name.slice(0, name.lastIndexOf('.')),
          },
        });
        this.editTitle();
      }

      this.displayAddItemPopup = false;

      const cardId = this.currentCard.id || this.newCardId;
      try {
        // Prepare data for sending to the backend
        const fileData = new FormData();
        fileData.append('card-item', file);
        await this.$store.dispatch('files/uploadFile', {
          cardId,
          relatedCollectionId: this.selectedCollection,
          filename: name,
          fileData,
          file,
        });
      }
      catch (err) {
        if (err.message === 'Maximum file size exceeded!') {
          this.setNotification('File is too big! Maximum size is 50MB.');
        }
        else {
          this.setError({ error: err });
        }
      }
      finally {
        this.fileIsUploading = false;
      }
    },
    async hideAddItemPopup(e) {
      if (this.waitingForId) return; // do not execute this more than once

      if (this.currentCard.id === 0) {
        this.waitingForId = true;
        this.newCardId = await this.newCardIdPromise;
        this.setNewCard();
        this.$goToCard({ id: this.newCardId });
        this.editTitle();
        this.waitingForId = false;
      }

      this.displayAddItemPopup = false;
    },
    setNewCard() {
      if (!this.newCardObject.title) this.newCardObject.title = 'draft';
      if (!this.newCardObject.id) this.newCardObject.id = this.newCardId;
      if (this.newCardObject.relatedCollectionId) this.newCardObject.relatedCollectionId = this.selectedCollection || this.selectedCollectionFallback;
      this.$store.commit('cards/SetCard', this.newCardObject);
    },
    deleteCard() {
      this.$buefy.dialog.confirm({
        message: `About to delete <em>${this.currentCard.title}</em>. Are you sure?<br><br>
                  <em style="font-size: 0.85rem;">Note that this cannot be undone.</em><br>`,
        cancelText: 'Cancel',
        confirmText: 'Yes',
        type: 'is-danger',
        hasIcon: false,
        onConfirm: () => {
          let index = this.openItems.findIndex(entry => entry.item.id === this.currentCard.id && entry.item.type === 'card');
          if (index + 1 === this.openItems.length) index -= 1; // If it's the last item, go to previous index

          this.$deleteCard(this.currentCard.id);

          if (this.openItems.length === 0) this.$goToCollection({ id: this.selectedCollection });
          else if (this.openItems[index].item.type === 'card') this.$goToCard(this.openItems[index].item);
          else if (this.openItems[index].item.type === 'note') this.$goToNote(this.openItems[index].item);
        },
      });
    },
    addKeyword(event) {
      if (event.key !== 'Tab') {
        this.$refs.newKeywordInput.blur();
        this.newKeywordHasFocus = false;
      }
      if (!this.newKeyword) return;

      if (this.newKeyword.includes(',')) {
        this.$buefy.toast.open({
          message: 'Keywords cannot contain commas!',
          duration: 2500,
          position: 'is-bottom',
          type: 'is-warning',
        });
        this.newKeyword = null;
        return;
      }

      if (this.currentCard.keywords && this.currentCard.keywords.includes(this.newKeyword)) {
        this.$buefy.toast.open({
          message: 'Cannot add a keyword twice!',
          duration: 2500,
          position: 'is-bottom',
          type: 'is-warning',
        });
        this.newKeyword = null;
        return;
      }

      this.$addKeyword({
        id: this.currentCard.id,
        keyword: this.newKeyword,
      });
      this.newKeyword = null;
    },
    cancelAddKeyword() {
      this.newKeyword = null;
      this.$refs.newKeywordInput.blur();
      this.newKeywordHasFocus = false;
    },
    deleteKeyword(keyword) {
      this.$deleteKeyword({
        id: this.currentCard.id,
        keyword,
      });
    },
    async setupNewCard() {
      this.newCardId = null;
      const relatedCollectionId = this.selectedCollection || this.selectedCollectionFallback;
      this.newCardObject = Object.assign({}, this.currentCard, { relatedCollectionId, dateCreated: this.$getCurrentUTC() });
      this.newCardIdPromise = this.getNewCardId(this.newCardObject);
      this.addItem();

      // If we already have url and title (via extension generally), process this
      if (this.$route.query.url) {
        this.handleAddUrlFromExtension();
      }
    },
    hideMenu() {
      this.menuHidden = true;
    },
  },
  watch: {
    currentCard(newCard, oldCard) {
      // It could be the case that the card is deleted from another client session. So if newCard is undefined, move away.
      if (!newCard) {
        this.$goToCollection({ id: oldCard.relatedCollectionId });
        return;
      }

      if (Object.keys(newCard).length) {
        this.editor.setContent(newCard.description);
        this.hideMenu();
        this.AddOpenItem(newCard);
        if (newCard.id === 0) this.setupNewCard();

        else {
          // else condition to prevent issues with new cards (accidental storing of scroll position for temp id of 0 etc)
          const scrollToPosition = this.$store.state.appState.scrollState.cardDescription[this.currentCard.id];
          if (scrollToPosition) this.$refs.editor.$el.scrollTo(0, scrollToPosition);
        }
      }

      if (oldCard.id === 0) this.CloseOpenItem({ id: oldCard.id, type: 'card' });
    },
  },
  async mounted() {
    if (Object.keys(this.currentCard).length) {
      this.editor.setContent(this.currentCard.description);
      this.AddOpenItem(this.currentCard);
      if (this.currentCard.id === 0) this.setupNewCard();

      else {
        // else condition to prevent issues with new cards (accidental storing of scroll position for temp id of 0 etc)
        const scrollToPosition = this.$store.state.appState.scrollState.cardDescription[this.currentCard.id];
        await this.$nextTick();
        if (scrollToPosition) this.$refs.editor.$el.scrollTo(0, scrollToPosition);
      }
    }
  },
  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/colors';
@import '~styles/fonts';
@import '~styles/layouts';

// CONTAINER
.card-container {
  position: relative;
  overflow: hidden;
  cursor: default;
  height: 100%;
  background-color: $primary-light;
  flex-grow: 1;
  flex-shrink: 2;
}

// CONTENT
.card-contents {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0;
  text-align: left;
  overflow-y: hidden;
}
.card-subheader {
  font-size: 0.9rem;
  font-family: $quattrocentoSans;
  font-variant: small-caps;
  font-style: normal;
  color: $primary-dark;
  padding: 1rem 0 0.25rem;
}

// BODY
.card-body {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;
  height: 100%;
  // max-height: calc(100vh - 5rem - 3rem - 0.5rem); // all height minus tabs (est. 5rem) minus card dates container minus some additional space [is unnecessary it seems]
  // padding: 0 4rem;
  overflow-y: auto;
  overflow-y: hidden;
  overflow-x: hidden;
}
.card-body-main {
  flex-grow: 1;
  flex-shrink: 2;
  height: 1px; // To give flexbox a height to work with
  overflow-y: auto;
  padding: 0 4rem;
}

// TITLE
.card-title {
  padding: 1rem 0 0;
}
.card-title-text {
  cursor: text;
  border-radius: 0.25rem;
  padding: 0.15rem 0.25rem;
  margin-left: -0.25rem;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden; // shouldn't be such overflow anyways
}
.card-title-text:hover {
  background: rgba($primary, 0.5);
}
.title-input {
  outline: none;
  border: none;
  width: 100%;
  font-size: 1rem;
  line-height: 1.5rem;
  font-family: $bodyFont;
  color: $black;
  border-radius: 0.25rem;
  padding: 0.15rem 0.25rem;
  margin-left: -0.25rem;
}
.title-input:focus {
  background: rgba($primary, 0.5);
}

// CARD ACTIONS
.card-actions {
  position: absolute;
  top: 1.5rem;
  right: 5rem;
  font-size: 1.25rem;
}
.dots-menu {
  opacity: 0.5;
  cursor: pointer;
  margin-left: 0.25rem;
}
.dots-menu:hover {
  opacity: 0.8;
}
.delete-icon {
  cursor: pointer;
  transition: all 0.2s;
}
.delete-icon:hover {
  opacity: 1;
}
.collection-list-item-menu {
  position: absolute;
  width: 10rem;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
  background-color: #eeeeee !important;
  box-shadow: 1px 1px 3px 1px $primary-dark;
  border-radius: 0 0.25rem 0.25rem 0.25rem;
  font-size: 0.8rem;
  opacity: 1;
  padding: 0 !important;
  white-space: normal;
  z-index: 5;
  left: -3rem;
  top: 1.75rem;
}
.collection-list-item-menu-divider {
  border-bottom: 1px solid $primary;
  margin: 0.25rem 0;
}
.collection-list-item-menu-item {
  position: relative;
  cursor: pointer;
  user-select: none;
  width: 100%;
  padding: 0.25rem 0.5rem !important;
  font-family: $headerFont;
}
.collection-list-item-menu-item:first-child {
  margin-top: 0.25rem;
}
.collection-list-item-menu-item:last-child {
  margin-bottom: 0.25rem;
}
.collection-list-item-menu-item:hover {
  background-color: $primary-light;
}
.inactive {
  color: rgba($primary-dark, 0.5);
}

// ITEM
.card-item-container {
  position: relative;
  width: calc(100%); // All width minus icons and right hand side
  font-size: 0.75rem;
  font-style: italic;
  line-height: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card-item {
  cursor: pointer;
  position: relative;
}
.delete-icon {
  color: darkred;
}
.item-is-being-deleted {
  opacity: 1;
}
.add-item-text,
.move-card-text {
  cursor: pointer;
  font-style: italic;
  color: $primary-dark;
}
.move-card-text {
  opacity: 0;
  font-size: 0.75rem !important;
  transition: opacity 0.2s;
}
.add-item-text:hover,
.move-card-text:hover {
  color: $secondary-dark;
}
.modal-card-body {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.modal-card-body .section {
  padding: 3vh 1.5rem;
}
.url-input {
  border-radius: 0.25rem;
  border: 1px solid $primary-dark;
  margin-top: 0.5rem;
  padding: 0.5rem;
  width: 30vw;
  outline: none;
}
.modal-card-foot {
  display: flex;
  justify-content: center;
}
.skip-button {
  background-color: $primary;
  color: $secondary-dark;
}
.save-button {
  background-color: $secondary-dark;
  color: $primary;
}

// COLLECTION
.card-collection span {
  font-size: 0.85rem;
  cursor: pointer;
}
.card-collection:hover .move-card-text {
  opacity: 1;
}
.move-icon {
  opacity: 0;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 0.25rem;
}
.card-collection:hover .move-icon:hover {
  opacity: 1;
}

// CARD DATES
.card-dates-container {
  // position: absolute;
  // bottom: 3rem;
  width: 25rem;
  height: 4.5rem;
  padding: 0.25rem 4rem;
  font-size: 0.7rem;
  border-top: 1px solid $primary;
  flex-grow: 0;
  flex-shrink: 0;
}
.card-date {
  display: flex;
  justify-content: space-between;
  color: $primary-dark;
}
.card-date span:last-child {
  color: $primary-dark;
  font-style: italic;
}

// DESCRIPTION
.card-description {
  position: relative;
  max-height: 35vh;
  width: 50rem;
  max-width: 100%;
  font-size: 0.9rem;
  font-family: $quattrocentoSans;
  transition: all 0.3s;
}
.floating-menu {
  @include floating-menu;
  margin-left: 0rem;
}
.is-active {
  @include is-active;
}
.menu-item {
  @include menu-item;
}
.menu-item:hover {
  opacity: 1;
}
.editor {
  min-height: 3rem;
  max-height: calc(30vh - 0.5rem);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.25rem;
  border-left: 1px solid $primary;
  background-color: rgba($primary, 0.15);
  cursor: text;
  @include prosemirror-for-editor;

  ::v-deep .ProseMirror {
    min-height: 2.5rem;
    height: auto;
  }
}

// KEYWORDS
.card-keywords {
  position: relative;
  width: 50rem;
  max-width: 100%;
  // border: 1px solid green;
  padding: 0.5rem 0;
}
.keyword-container {
  display: inline-block;
}
.keyword {
  background: darken($white, 5);
  padding: 0.15rem;
  padding-left: 0.25rem;
  font-family: $quattrocentoSans;
  font-size: 0.75rem;
  font-weight: 100;
  font-style: italic;
  cursor: default;
}
.keyword i {
  opacity: 0.1;
  position: relative;
  left: 2px;
  cursor: pointer;
}
.keyword:hover i {
  opacity: 0.75;
}
.new-keyword {
  border: none;
  outline: none;
  background: transparent;
  width: 7em;
  font-weight: 100;
  font-size: 0.75rem;
  font-style: italic;
  padding: 0.15rem;
  font-family: $quattrocentoSans;
}
.new-keyword::placeholder {
  opacity: 0.75;
}
.new-keyword:focus {
  width: auto;
}
.keyword-triangle {
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 0.62rem solid transparent;
  border-right: 0.62rem solid transparent;
  border-bottom: 0.62rem solid $darkgrey;
  border-bottom: 0.62rem solid darken($white, 5);
  position: relative;
  top: 1.5px;
  right: 0.4rem;
  transform: rotate(90deg);
}

// SECTION
.card-section {
  position: relative;
  display: inline-block;
  font-size: 0.8rem;
  font-family: $headerFont;
  margin-right: 0.25rem;
  padding: 0.1rem 0.5rem;
  border-radius: 1rem;
  border: 1px solid $primary;
  cursor: pointer;
  transition: background-color 0.2s;
}
.card-section:hover,
.active-card-section {
  background-color: $primary;
}
.add-card-section {
  outline: none;
  border: 1px solid $primary;
  border-radius: 1rem;
  padding: 0.15rem 0.5rem;
  width: 6rem;
}
.add-card-section::placeholder {
  font-style: italic;
  font-size: 0.7rem;
}
.remove-card-section {
  position: absolute;
  right: -0.4rem;
  top: -0.4rem;
  font-size: 0.8rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: $primary-dark;
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
}
.active-card-section:hover .remove-card-section {
  opacity: 1;
}
.remove-card-section:hover {
  background-color: darken($primary-dark, 30);
  color: $primary-dark;
}
</style>
